import React, { useCallback, useEffect, useState } from 'react';
import {
    CustomDivider,
    InformationCard,
    InformationCardProps,
    NoteInformation,
    Previous,
    Switch,
    PriceLigne,
    CardHeader,
    CardSection,
    TextIcon,
} from '@europrocurement/flexy-components';
import {
    CollectionDossierFormalite,
    DossierCollectionFormaliteApiObject,
    DossierJsonldRead,
    PrescripteurApi,
    SousClientApi,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { castIriToId } from '@europrocurement/l2d-utils';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { FichiersDossierJsonldDocumentfileRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { FaOptionIcon, waitFormalisteIcon } from '@europrocurement/l2d-icons';
import { faCircleCheck, faPaperPlane, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiTiers';

import { Container } from '@mui/system';
import BillingInfo, {
    BILLING_TYPE_VD,
    BILLING_TYPE_VI_L2D,
    BILLING_TYPE_VI_PRESCRIBER,
} from '../../Dossiers/components/detail/BillingInfo/BillingInfo';
import { DocToSend } from './Docs/DocToSend';
import SelectSociete from './SelectSociete';
import { useParcoursFormalite } from '../provider';
import { updatePrescriptionCollection } from '../provider/functions';

type Items = InformationCardProps['items'];

type RecapFormalityShowProps = {
    collection: CollectionDossierFormalite;
    fichiers: FichiersDossierJsonldDocumentfileRead[];
    onPrevious?: () => void;
    onSubmit?: (collection: CollectionDossierFormalite) => void;
    onChange?: (
        collection: CollectionDossierFormalite,
        refInterne: string | null,
        commentaires: string | null,
        dossierUrgent: boolean | null,
        billingType: number,
        recipientId: number,
    ) => void;
    prescripteur: PrescripteurApi;
    sousClient?: SousClientApi | undefined;
    sousClients: SousClientApi[];
    commentairesDossier?: string | null;
};
type RecapArticlesLineProps = {
    montant: number;
    prixEnAttente: boolean;
};
type RecapArticlesLinesProps = {
    PubPubns: RecapArticlesLineProps;
    Vac: RecapArticlesLineProps;
    FrnFrs: RecapArticlesLineProps;
    Autre: RecapArticlesLineProps;
    withPrice: boolean;
};

const AdresseComposee = (
    adresse: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
): string => {
    const items = [
        adresse?.adresse1,
        adresse?.adresse2,
        adresse?.adresse3,
        adresse?.adresse4,
        adresse?.codePostal,
        adresse?.ville,
    ];

    return items.filter((item) => item !== undefined).join(' ');
};

export const FrenchPrice = (price: number | string | null | undefined) => {
    if (!price) {
        return '';
    }
    let value = price;
    if (typeof price === 'string') {
        value = parseFloat(price);
    }

    return (Math.floor(value * 100) / 100).toFixed(2).toString().replace('.', ',');
};
export const reduceLines = function (
    dossier: DossierCollectionFormaliteApiObject,
): RecapArticlesLinesProps {
    const montantReduit: RecapArticlesLinesProps = {
        PubPubns: { montant: 0, prixEnAttente: false },
        Vac: { montant: 0, prixEnAttente: false },
        FrnFrs: { montant: 0, prixEnAttente: false },
        Autre: { montant: 0, prixEnAttente: false },
        withPrice: false,
    };

    if (!dossier.prestations || dossier.prestations.length === 0) {
        return montantReduit;
    }
    dossier.prestations.map((prestation) => {
        if (prestation.articles) {
            prestation.articles.map((article) => {
                if (article.tarif && !article.fMasqueCalcul && article.factArticle.rubArticle) {
                    switch (article.factArticle.rubArticle) {
                        case 'FRN':
                        case 'FRS':
                            montantReduit.FrnFrs.montant +=
                                Number(article.tarif) * Number(article.quantite);
                            montantReduit.withPrice = true;
                            break;
                        case 'VAC':
                            montantReduit.Vac.montant +=
                                Number(article.tarif) * Number(article.quantite);
                            montantReduit.withPrice = true;
                            break;
                        case 'PUB':
                        case 'PUBNS':
                            montantReduit.PubPubns.montant +=
                                Number(article.tarif) * Number(article.quantite);
                            montantReduit.withPrice = true;
                            break;
                        default:
                            montantReduit.Autre.montant +=
                                Number(article.tarif) * Number(article.quantite);
                            montantReduit.withPrice = true;
                            break;
                    }
                } else if (!article.tarif) {
                    switch (article.factArticle.rubArticle) {
                        case 'FRN':
                        case 'FRS':
                            montantReduit.FrnFrs.prixEnAttente = true;
                            break;
                        case 'VAC':
                            montantReduit.Vac.prixEnAttente = true;
                            break;
                        case 'PUB':
                        case 'PUBNS':
                            montantReduit.PubPubns.prixEnAttente = true;
                            break;
                        default:
                            montantReduit.Autre.prixEnAttente = true;
                            break;
                    }
                }
                return montantReduit;
            }, []);
        }
        return montantReduit;
    });

    return montantReduit;
};

const RecapFormalityShow: React.FunctionComponent<RecapFormalityShowProps> = function ({
    collection,
    fichiers,
    prescripteur,
    sousClient = prescripteur,
    sousClients,
    onPrevious,
    onSubmit,
    onChange,
    commentairesDossier,
}) {
    if (!collection.dossiers || collection.dossiers.length === 0) {
        console.error("Demande d'affichage d'une collection sans dossier en récap.");
        throw new Error("Demande d'affichage d'une collection sans dossier en récap.");
    }
    if (collection.dossiers.length > 1) {
        console.error("Demande d'affichage d'une collection multiples en récap.");
        throw new Error("Demande d'affichage d'une collection multiples en récap.");
    }

    const { containerWidth } = useParcoursFormalite();

    const premierDossier = collection?.dossiers[0];
    const { dossierApi, collectionApi } = useFormaliteService();

    // premierDossier.societeRef ? premierDossier.societeRef : null,
    const [commentaires, setCommentaires] = React.useState<string | null>(
        commentairesDossier || null,
    );
    const [innerDossier, setInnerDossier] = React.useState<DossierJsonldRead | null>(null);
    const [dossierUrgent, setDossierUrgent] = React.useState<boolean>(collection.urgent as boolean);

    if (premierDossier.client === undefined || premierDossier.client === null) {
        console.error("Demande d'affichage d'un dossier sans client en récap");
        throw new Error("Demande d'affichage d'un dossier sans client en récap");
    }
    if (premierDossier.clientOpere === undefined || premierDossier.clientOpere === null) {
        console.error("Demande d'affichage d'un dossier sans client opéré en récap");
        throw new Error("Demande d'affichage d'un dossier sans client opéré en récap");
    }
    const [refInterne, setRefInterne] = React.useState<string | null>(
        premierDossier.societeRef ? premierDossier.societeRef : null,
    );
    const clientId = castIriToId(premierDossier.client as unknown as string) as number;
    const defaultBillingType = premierDossier.typeFacturation as number;

    const clientOpereId = castIriToId(premierDossier.clientOpere as unknown as string) as number;

    const [recipientId, setRecipientId] = useState<number>(clientId);

    const [billingType, setBillingType] = useState<number>(defaultBillingType);

    const clientAddresses = sousClients.map((client) => ({
        clientId: client.id as number,
        label: client.raisonSociale,
        siren: client.siren as string,
        address: AdresseComposee(client.adresses[0]),
    }));

    const hasClientAddress =
        prescripteur.id === clientId ||
        sousClients.find((client) => client.id === clientId) !== undefined;

    if (!hasClientAddress) {
        clientAddresses?.push({
            clientId,
            label: sousClient.raisonSociale,
            siren: sousClient.siren ? sousClient.siren : '',
            address: AdresseComposee(sousClient.adresses[0]),
        });
    }
    const theme = useTheme();
    const onChangeHandler = useCallback(() => {
        if (onChange) {
            onChange(collection, refInterne, commentaires, dossierUrgent, billingType, recipientId);
        }
    }, [commentaires, collection, dossierUrgent, onChange, refInterne, billingType, recipientId]);

    React.useEffect(() => {
        onChangeHandler();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossierUrgent, refInterne, billingType, recipientId, commentaires]);
    const onSubmitHandler = useCallback(() => {
        if (onSubmit) {
            onSubmit(collection);
        }
    }, [collection, onSubmit]);

    React.useEffect(() => {
        dossierApi.apiDossiersIdGet({ id: `${premierDossier.id}` }).then(({ data }) => {
            setInnerDossier(data);
        });
    }, [dossierApi, premierDossier.id]);

    let cardItemsSocite: InformationCardProps['items'] = [
        {
            label: 'Société concernée',
            value: premierDossier.societeDenomination,
        },
    ];

    if (premierDossier.societeSiren) {
        cardItemsSocite = [
            ...cardItemsSocite,
            {
                label: 'Numéro Siren',
                value: premierDossier.societeSiren,
            },
        ];
    }

    const cardItemsFormalite: () => Items = () => {
        if (!premierDossier.prestations || premierDossier.prestations.length === 0) {
            return [];
        }
        return premierDossier.prestations?.map((prestation, index) => ({
            label: `Formalité n°${index + 1}`,
            value: prestation.libelle,
        }));
    };

    const lignesArticle: RecapArticlesLinesProps = reduceLines(premierDossier);

    const cardProps = {
        backgroundColor: theme.palette.background.dark,
        boxShadow: '0px 2px 2.5px 0px rgba(193, 202, 215, 0.25)',
    };

    const informationsDocuments: InformationCardProps['items'] =
        innerDossier?.documents
            ?.map((typedoc) => ({
                ...typedoc,
                libelle: typedoc.libelle || 'Document non typé',
                id: typedoc.id || -1,
            }))
            .map((requiredType) => {
                const docOk = fichiers?.find(
                    (item) => item.typeDocDossier && item.typeDocDossier.id === requiredType.id,
                );
                if (docOk) {
                    return {
                        label: requiredType.libelle,
                        value: (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '5px',
                                    alignItems: 'start',
                                }}
                            >
                                <Typography
                                    color={theme.palette.primary.dark}
                                    sx={{ fontSize: '15px' }}
                                >
                                    {docOk.fichier}
                                </Typography>
                                <Typography
                                    color="text.gris50"
                                    sx={{ fontSize: '10px' }}
                                >
                                    <FaOptionIcon
                                        icon={faCircleCheck}
                                        color="text.gris70"
                                    />
                                </Typography>
                            </Box>
                        ),
                    };
                }
                return {
                    label: requiredType.libelle,
                    value: (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '5px',
                                alignItems: 'start',
                            }}
                        >
                            <Typography
                                color="text.gris50"
                                sx={{ fontSize: '15px' }}
                            >
                                Manquant
                            </Typography>
                            <Typography
                                color="text.gris50"
                                sx={{ fontSize: '10px' }}
                            >
                                <FaOptionIcon
                                    icon={faWarning}
                                    color={theme.palette.warning.dark}
                                />
                            </Typography>
                        </Box>
                    ),
                    MarkAsEmpty: true,
                };
            }) || [];

    const handleRecipientChange = (newRecipientId?: number) => {
        setRecipientId(
            newRecipientId ||
                (prescripteur.id !== clientOpereId ? clientOpereId : (sousClients[0].id as number)),
        );
    };

    const handleBillingTypeChange = (newBillingType: number) => {
        setBillingType(newBillingType);
    };

    useEffect(() => {
        let newBillingType: number = billingType;

        if (prescripteur.id === recipientId) {
            newBillingType = BILLING_TYPE_VI_PRESCRIBER;
        } else {
            const sousClientCollection = sousClients?.find((t) => t.id === recipientId);

            if (sousClientCollection) {
                newBillingType = sousClientCollection.venteIndirecte
                    ? BILLING_TYPE_VI_L2D
                    : BILLING_TYPE_VD;
            }
        }

        const newClientId = recipientId || clientId;
        const hasChanges = newBillingType !== billingType || newClientId !== clientId;

        if (!hasChanges) {
            return;
        }

        if (newBillingType !== billingType) {
            setBillingType(newBillingType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipientId]);

    let billingBody = (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
            <CircularProgress />
        </Box>
    );
    const mainAddressePrescripteur = prescripteur.adresses.find((address) => address.defaut);
    if (prescripteur.id && mainAddressePrescripteur) {
        billingBody = (
            <BillingInfo
                prescriberId={prescripteur.id}
                recipientId={recipientId}
                prescriberLabel={prescripteur.raisonSociale as string}
                prescriberSiren={prescripteur.siren as string}
                prescriberAddress={AdresseComposee(mainAddressePrescripteur)}
                onRecipientChange={handleRecipientChange}
                addresses={clientAddresses}
                billingType={billingType}
                onBillingTypeChange={handleBillingTypeChange}
                canEdit
                canEditBillingType
            />
        );
    }

    return (
        <>
            {onPrevious ? <Previous onClick={onPrevious} /> : null}
            <Typography
                variant="l2dh4"
                sx={{
                    marginBottom: '10px',
                }}
            >
                Récapitulatif du dossier #{premierDossier.numero}
            </Typography>
            <Grid
                container
                flexWrap="nowrap"
                spacing={6}
            >
                <Grid
                    item
                    lg={8}
                    display="grid"
                    gap="16px"
                >
                    <CardSection
                        sx={{ margin: 0 }}
                        sxContent={{ gap: '24px', display: 'grid' }}
                        header={
                            <CardHeader
                                headerSx={{ paddingTop: '24px' }}
                                sx={{
                                    minHeight: '36px',
                                    borderLeft: `solid 2px ${theme.palette.primary.main}`,
                                }}
                                title={
                                    <Typography variant="l2dh6">Paramètres du dossier</Typography>
                                }
                            />
                        }
                    >
                        <Box sx={{ gap: '4px', display: 'grid' }}>
                            <Box>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Référence interne du dossier
                                </Typography>
                                <Typography
                                    sx={{ marginLeft: '5px' }}
                                    variant="l2dbodys"
                                    color="text.secondary"
                                >
                                    (facultatif)
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                defaultValue={refInterne}
                                placeholder="Saisissez une référence pour ce dossier"
                                sx={{ gap: '8px' }}
                                onBlur={(
                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                                ) => {
                                    if (e.target.value && e.target.value !== '') {
                                        setRefInterne(e.target.value);
                                    } else {
                                        setRefInterne(null);
                                    }
                                }}
                            />
                        </Box>
                        <Box sx={{ gap: '4px', display: 'grid' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    padding: '10px 10px 10px 0px',
                                    alignItems: 'start',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Marquer ce dossier comme urgent ?
                                </Typography>

                                <Switch
                                    size="small"
                                    aria-label="switch"
                                    value={dossierUrgent}
                                    onChange={(e, checked: boolean) => {
                                        setDossierUrgent(checked);
                                    }}
                                />
                            </Box>
                            <Box>
                                <NoteInformation
                                    title="Cette option entraînera des frais de traitement supplémentaires."
                                    color={{
                                        bg: '#ebf8fd',
                                        border: '#b0e4f7',
                                    }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ gap: '4px', display: 'grid' }}>
                            <Box>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Commentaires
                                </Typography>
                                <Typography
                                    sx={{ marginLeft: '5px' }}
                                    variant="l2dbodys"
                                    color="text.secondary"
                                >
                                    (facultatif)
                                </Typography>
                            </Box>
                            <TextField
                                placeholder="Ajouter des informations complémentaires au dossier"
                                multiline
                                style={{
                                    width: '100%',
                                }}
                                maxRows={Infinity}
                                defaultValue={commentaires}
                                onBlur={(
                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                                ) => {
                                    if (e.target.value && e.target.value !== '') {
                                        setCommentaires(e.target.value);
                                    } else {
                                        setCommentaires(null);
                                    }
                                }}
                            />
                        </Box>
                    </CardSection>

                    <CardSection
                        sx={{ margin: 0 }}
                        sxContent={{ gap: '24px', display: 'grid' }}
                        header={
                            <CardHeader
                                title={<Typography variant="l2dh6">Facturation</Typography>}
                                headerSx={{ paddingTop: '24px' }}
                                sx={{
                                    minHeight: '36px',
                                    borderLeft: `solid 2px ${theme.palette.primary.main}`,
                                }}
                            />
                        }
                    >
                        {billingBody}
                    </CardSection>
                    <InformationCard
                        title="Société"
                        subtitle=""
                        items={cardItemsSocite}
                        sx={{ ...cardProps }}
                        listComponent={
                            <Container maxWidth={containerWidth}>
                                <SelectSociete
                                    title=""
                                    description=""
                                    societeSelectedDefault={[
                                        String(+premierDossier.clientOpere.split('/').pop() || ''),
                                    ]}
                                    onSelect={async (ids) => {
                                        if (ids.length > 0 && collection.id) {
                                            setRecipientId(ids[0]);
                                            await updatePrescriptionCollection(
                                                collection.id,
                                                collectionApi,
                                                ids[0],
                                            );
                                        }
                                    }}
                                    submitButtonLabel="Valider"
                                />
                            </Container>
                        }
                    />
                    <InformationCard
                        title="Formalité"
                        items={cardItemsFormalite()}
                        sx={{ ...cardProps }}
                        subtitle="Pour modifier cette formalité, contactez votre formaliste"
                    />
                    {innerDossier &&
                    innerDossier.documents &&
                    innerDossier.documents.filter(
                        (item) => item.statut && castIriToId(item.statut) === 7,
                    ) ? (
                        <InformationCard
                            title="Documents"
                            sx={{ ...cardProps }}
                            items={informationsDocuments}
                            labelCount={{
                                plurialLabel: 'documents transmis',
                                singularLabel: 'document transmis',
                            }}
                            listComponent={
                                <Box sx={{ padding: '0 32px' }}>
                                    <DocToSend dossier={innerDossier} />
                                </Box>
                            }
                        />
                    ) : null}
                </Grid>
                <Grid
                    item
                    lg={4}
                    display="flex"
                    flexDirection="column"
                    gap="16px"
                >
                    <Box
                        className="information-card"
                        sx={{ ...cardProps, padding: '24px  32px' }}
                    >
                        <Typography
                            variant="l2dbodyl"
                            sx={{ fontWeight: 400 }}
                        >
                            Estimation
                        </Typography>
                        <Box sx={{ marginTop: '16px' }}>
                            {lignesArticle.FrnFrs.montant ? (
                                <PriceLigne
                                    label="Frais de registre"
                                    price={`${FrenchPrice(lignesArticle.FrnFrs.montant)} €${
                                        lignesArticle.FrnFrs.prixEnAttente ? (
                                            <FaOptionIcon
                                                size="sm"
                                                {...waitFormalisteIcon.props}
                                            />
                                        ) : (
                                            ''
                                        )
                                    }`}
                                />
                            ) : null}
                            {lignesArticle.FrnFrs.prixEnAttente &&
                                !lignesArticle.FrnFrs.montant && (
                                    <PriceLigne
                                        label="Frais de registre"
                                        price={
                                            <FaOptionIcon
                                                size="sm"
                                                {...waitFormalisteIcon.props}
                                            />
                                        }
                                    />
                                )}
                            {lignesArticle.PubPubns.montant ? (
                                <PriceLigne
                                    label="Annonce légale"
                                    price={`${FrenchPrice(lignesArticle.PubPubns.montant)} €${
                                        lignesArticle.FrnFrs.prixEnAttente ? (
                                            <FaOptionIcon
                                                size="sm"
                                                {...waitFormalisteIcon.props}
                                            />
                                        ) : (
                                            ''
                                        )
                                    }`}
                                />
                            ) : null}
                            {lignesArticle.PubPubns.prixEnAttente &&
                                !lignesArticle.PubPubns.montant && (
                                    <PriceLigne
                                        label="Annonce légale"
                                        price={
                                            <FaOptionIcon
                                                size="sm"
                                                {...waitFormalisteIcon.props}
                                            />
                                        }
                                    />
                                )}
                            {lignesArticle.Vac.montant ? (
                                <PriceLigne
                                    label="Honoraires"
                                    price={`${FrenchPrice(lignesArticle.Vac.montant)} €${
                                        lignesArticle.FrnFrs.prixEnAttente ? (
                                            <FaOptionIcon
                                                size="sm"
                                                {...waitFormalisteIcon.props}
                                            />
                                        ) : (
                                            ''
                                        )
                                    }`}
                                />
                            ) : null}
                            {lignesArticle.Vac.prixEnAttente && !lignesArticle.Vac.montant && (
                                <PriceLigne
                                    label="Honoraires"
                                    price={
                                        <FaOptionIcon
                                            size="sm"
                                            {...waitFormalisteIcon.props}
                                        />
                                    }
                                />
                            )}
                            {lignesArticle.Autre.montant ? (
                                <PriceLigne
                                    label="Divers"
                                    price={`${FrenchPrice(lignesArticle.Autre.montant)} €${
                                        lignesArticle.FrnFrs.prixEnAttente ? (
                                            <FaOptionIcon
                                                size="sm"
                                                {...waitFormalisteIcon.props}
                                            />
                                        ) : (
                                            ''
                                        )
                                    }`}
                                />
                            ) : null}
                            {lignesArticle.Autre.prixEnAttente && !lignesArticle.Autre.montant && (
                                <PriceLigne
                                    label="Divers"
                                    price={
                                        <FaOptionIcon
                                            size="sm"
                                            {...waitFormalisteIcon.props}
                                        />
                                    }
                                />
                            )}

                            {lignesArticle.FrnFrs.prixEnAttente ||
                            lignesArticle.PubPubns.prixEnAttente ||
                            lignesArticle.Vac.prixEnAttente ||
                            lignesArticle.Autre.prixEnAttente ? (
                                <Box
                                    sx={{
                                        marginTop: '15px',
                                        background: 'pink',
                                        alignItems: 'center',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        backgroundColor: theme.palette.grey[200],
                                    }}
                                >
                                    <Typography variant="l2dbodyxs">
                                        Votre dossier ne peut pas être entièrement devisé car
                                        certains éléments nécessitent l&apos;analyse de nos équipes.
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                        {lignesArticle.withPrice ? (
                            <>
                                <CustomDivider style={{ opacity: '10%', height: '1px' }} />
                                <PriceLigne
                                    labelTypographyProps={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                    priceTypographyProps={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                    label="Prix HT"
                                    price={`${FrenchPrice(collection.ht)} €`}
                                />
                                <PriceLigne
                                    labelTypographyProps={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                    priceTypographyProps={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                    label="TVA"
                                    price={`${FrenchPrice(collection.tva)} €`}
                                />
                                <CustomDivider style={{ opacity: '10%', height: '1px' }} />
                                <PriceLigne
                                    labelTypographyProps={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                    priceTypographyProps={{
                                        variant: 'l2dh6',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                    label="Prix TTC"
                                    price={`${FrenchPrice(collection.ttc)} €`}
                                    sx={{ paddingTop: '12px', paddingBottom: '12px' }}
                                />
                            </>
                        ) : null}
                    </Box>
                    <Box>
                        <Button
                            color="primary"
                            variant="contained"
                            sx={{
                                width: '100%',
                                backgroundColor: theme.palette.primary.dark,
                                height: '56px',
                                transition: 'background-color ease-in-out 300ms',
                                ':hover': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            }}
                            onClick={onSubmitHandler}
                        >
                            <TextIcon
                                icon={{ props: { icon: faPaperPlane } }}
                                gap="6px"
                            >
                                Envoyer le dossier
                            </TextIcon>
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default RecapFormalityShow;
